import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ArticleManager() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category: 'General',
    tags: '',
    published: true
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles?published_only=false`);
      setArticles(response.data);
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/articles`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      resetForm();
      fetchArticles();
    } catch (err) {
      console.error('Error saving article:', err);
      alert('Failed to save article');
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt,
      featured_image: article.featured_image || '',
      category: article.category || 'General',
      tags: article.tags.join(', '),
      published: article.published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchArticles();
    } catch (err) {
      console.error('Error deleting article:', err);
      alert('Failed to delete article');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      featured_image: '',
      category: 'General',
      tags: '',
      published: true
    });
    setEditingArticle(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading articles...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-light">MANAGE ARTICLES</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-2 bg-white text-black font-medium hover:bg-gray-200 transition-colors"
        >
          {showForm ? 'CANCEL' : 'NEW ARTICLE'}
        </button>
      </div>

      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 p-6 mb-8">
          <h3 className="text-xl font-light mb-6">{editingArticle ? 'EDIT ARTICLE' : 'NEW ARTICLE'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Excerpt *</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                required
                rows="2"
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Content *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                required
                rows="8"
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                  className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                  placeholder="https://example.com/image.jpg"
                />
                {formData.featured_image && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Image Preview:</p>
                    <img 
                      src={formData.featured_image} 
                      alt="Preview" 
                      className="w-full h-32 object-cover rounded border border-zinc-700"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Tags (comma separated)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="AI, Trading, Forex"
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="published" className="text-sm text-gray-400">Publish immediately</label>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-2 bg-white text-black font-medium hover:bg-gray-200 transition-colors"
              >
                {editingArticle ? 'UPDATE' : 'CREATE'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 border border-zinc-700 text-white hover:bg-zinc-800 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {articles.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No articles yet. Create your first article!</div>
        ) : (
          articles.map((article) => (
            <div key={article.id} className="bg-zinc-900 border border-zinc-800 p-6">
              <div className="flex items-start gap-4">
                {/* Featured Image Thumbnail */}
                {article.featured_image && (
                  <div className="flex-shrink-0">
                    <img 
                      src={article.featured_image} 
                      alt={article.title}
                      className="w-32 h-24 object-cover rounded border border-zinc-700"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Article Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-medium">{article.title}</h3>
                    {!article.published && (
                      <span className="text-xs px-2 py-1 bg-yellow-900/30 text-yellow-400 border border-yellow-900">DRAFT</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mb-3">{article.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{article.category}</span>
                    <span>•</span>
                    <span>{new Date(article.created_at).toLocaleDateString()}</span>
                    {article.tags.length > 0 && (
                      <>
                        <span>•</span>
                        <span>{article.tags.join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(article)}
                    className="px-4 py-2 border border-zinc-700 text-sm hover:bg-zinc-800 transition-colors"
                  >
                    EDIT
                  </button>
                  <button
                    onClick={() => handleDelete(article.id)}
                    className="px-4 py-2 border border-red-900 text-red-400 text-sm hover:bg-red-900/20 transition-colors"
                  >
                    DELETE
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ArticleManager;
